import { useEffect, useRef, useState } from 'react';
import { InformationCircle, Pencil, Plus } from 'assets/icons';
import PanelCard, { cardIconButtonClass } from 'components/card/PanelCard';
import useModal from 'components/modal/useModal';
import Tooltip from 'components/tooltips/Tooltips';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';

export const BILLING_CONTACTS_UPDATED = 'billing_contacts_updated';
const EMPLOYEE_CREATED = 'employee_created';

/** "Prénom NOM", or an empty string. */
const fullName = (person) =>
  [person?.firstname, (person?.lastname ?? '').toUpperCase()]
    .filter(Boolean)
    .join(' ');

const receivesMember = (member) => member.invoices || member.reminders;

// French fallbacks of each group's texts, read under card.groups.<group>.
const GROUP_FALLBACKS = {
  members: {
    title: 'Inscrit sur Jobbiz',
    info: 'Membres de l’entreprise, avec un compte Jobbiz, qui reçoivent les factures ou les relances.',
    empty: 'Aucun membre ne reçoit les factures ni les relances.',
  },
  externals: {
    title: 'Non inscrit sur Jobbiz',
    info: 'Personnes sans compte Jobbiz, ajoutées seulement pour recevoir les factures ou les relances.',
    empty: 'Aucun contact sans compte.',
  },
};

/**
 * Who receives the invoices and the reminders of the company (JBWEB-1285):
 * members with an account, then contacts without one. Read only: every change
 * goes through a modal, opened from the "+" or a row's pencil.
 */
const CompanyBillingNotificationsCard = ({
  idCompany,
  canEdit,
  refreshKey = 0,
}) => {
  const { language, setModalData, modalDataReturn, setModalDataReturn } =
    UseContext();
  const { toggle } = useModal();
  const [contacts, setContacts] = useState(null);
  const [status, setStatus] = useState('loading');
  const [readCount, setReadCount] = useState(0);
  // A return already in the context when the card mounts was meant for an
  // earlier screen: the first read covers it.
  const handledReturn = useRef(modalDataReturn);
  // The company whose contacts are on screen: read again, they stay there
  // while the new answer comes, and a failure leaves them.
  const loadedCompany = useRef(null);
  const text = (key, fallback) =>
    language.getValue(`companyBillingContacts.${key}`, fallback);

  useEffect(() => {
    setContacts(null);
  }, [idCompany]);

  useEffect(() => {
    if (!idCompany) return undefined;
    // A late answer for a company the panel has since left is dropped.
    let isCurrent = true;
    const isRefresh = loadedCompany.current === idCompany;
    if (!isRefresh) setStatus('loading');
    ApiDatabase.getCompanyBillingContacts(
      { idCompany },
      (response) => {
        if (!isCurrent) return;
        loadedCompany.current = idCompany;
        setContacts(response?.data ?? null);
        setStatus('ready');
      },
      (error) => {
        if (!isCurrent || error?.name === 'CanceledError' || isRefresh) return;
        setStatus('error');
      },
    );
    return () => {
      isCurrent = false;
    };
  }, [idCompany, readCount, refreshKey]);

  const readAgain = () => setReadCount((previous) => previous + 1);

  useEffect(() => {
    if (handledReturn.current === modalDataReturn) return;
    handledReturn.current = modalDataReturn;
    const type = modalDataReturn?.type;
    if (type === BILLING_CONTACTS_UPDATED) {
      setModalDataReturn({});
      readAgain();
    }
    // Left in the context: the members list consumes it to add the new
    // member to the panel's company once its tab is shown. Another company's
    // invitation changes nothing here.
    if (
      type === EMPLOYEE_CREATED &&
      String(modalDataReturn.idCompany) === String(idCompany)
    ) {
      readAgain();
    }
  }, [modalDataReturn]);

  const openModal = (type, data) => {
    setModalData(data);
    toggle(type);
  };

  const receivedText = (person) => {
    if (person.invoices && person.reminders)
      return text('card.receives.both', 'Factures, relances');
    if (person.invoices) return text('card.receives.invoices', 'Factures');
    if (person.reminders) return text('card.receives.reminders', 'Relances');
    return text('card.receives.none', 'Aucune notification');
  };

  const memberMention = (member) => {
    if (member.pending)
      return text('card.memberStatus.pending', 'Invitation en attente');
    return null;
  };

  // The "+" sits in the header, which folds the card: its click and its
  // Enter/Space would reach the header too and fold the card it opens from.
  const addButton = canEdit ? (
    <button
      type={'button'}
      aria-label={text('card.add', 'Ajouter un destinataire')}
      className={cardIconButtonClass}
      onClick={(event) => {
        event.stopPropagation();
        openModal('billing_contact_add', { idCompany });
      }}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <Plus wh={18} color={'#374151'} />
    </button>
  ) : null;

  const renderRow = (person, { key, mention, onEdit }) => {
    const name = fullName(person) || person.email;
    return (
      <li
        key={key}
        className={'py-2 flex justify-between gap-2 items-center'}
        data-testid={'billing-contact-row'}
      >
        <div className={'min-w-0'}>
          <div className={'flex gap-2 items-baseline min-w-0'}>
            <span className={'truncate text-gray-900'}>{name}</span>
            {mention ? (
              <span className={'text-xs text-gray-500 flex-shrink-0'}>
                {mention}
              </span>
            ) : null}
          </div>
          <div className={'truncate text-gray-700'} title={person.email}>
            {person.email}
          </div>
        </div>
        <div className={'flex items-center gap-3 flex-shrink-0'}>
          <span className={'text-sm text-gray-500'}>
            {receivedText(person)}
          </span>
          {canEdit ? (
            <button
              type={'button'}
              aria-label={text('card.edit', 'Modifier {name}').replace(
                '{name}',
                name,
              )}
              className={cardIconButtonClass}
              onClick={onEdit}
            >
              <Pencil wh={18} color={'#374151'} />
            </button>
          ) : null}
        </div>
      </li>
    );
  };

  const renderGroup = (group, rows) => {
    const groupText = (key) =>
      text(`card.groups.${group}.${key}`, GROUP_FALLBACKS[group][key]);
    const info = groupText('info');
    return (
      <section data-testid={`billing-contacts-${group}`}>
        <div
          className={
            'flex items-center justify-between gap-2 py-1 text-sm font-medium uppercase text-gray-500'
          }
        >
          <span>{groupText('title')}</span>
          {/* The icon has no text of its own: the tooltip is what explains
              who the group holds. As wide as a pencil, so both line up. */}
          <Tooltip text={info} placement={'top'}>
            <span
              role={'img'}
              aria-label={info}
              className={'flex w-8 justify-center flex-shrink-0'}
            >
              <InformationCircle wh={16} color={'#6B7280'} />
            </span>
          </Tooltip>
        </div>
        {rows.length > 0 ? (
          <ul className={'divide-y divide-gray-100'}>{rows}</ul>
        ) : (
          <div className={'py-2 text-sm text-gray-500'}>
            {groupText('empty')}
          </div>
        )}
      </section>
    );
  };

  const renderContent = () => {
    if (status === 'error') {
      return (
        <div className={'flex items-center justify-between gap-2'}>
          <div className={'text-red-500'}>
            {text(
              'card.loadError',
              'Les destinataires des factures n’ont pas pu être chargés.',
            )}
          </div>
          <button
            type={'button'}
            className={'text-gray-700 underline flex-shrink-0'}
            onClick={readAgain}
          >
            {text('card.retry', 'Réessayer')}
          </button>
        </div>
      );
    }
    // Only before the first answer: a list read again after a modal stays on
    // screen rather than flashing the loading line.
    if (!contacts) {
      return (
        <div className={'text-gray-500'}>
          {text('card.loading', 'Chargement des destinataires…')}
        </div>
      );
    }
    // Members who receive neither are listed by the api for the add modal's
    // search, not as recipients.
    const members = (contacts.members ?? []).filter(receivesMember);
    const externals = contacts.externals ?? [];
    if (members.length === 0 && externals.length === 0) {
      return (
        <div className={'text-gray-500'}>
          {text(
            'card.nobody',
            'Personne ne reçoit les factures ni les relances.',
          )}
        </div>
      );
    }
    return (
      <>
        {renderGroup(
          'members',
          members.map((member) =>
            renderRow(member, {
              key: `member-${member.idEmployee}`,
              mention: memberMention(member),
              onEdit: () =>
                openModal('billing_contact_member_edit', { idCompany, member }),
            }),
          ),
        )}
        {renderGroup(
          'externals',
          externals.map((contact) =>
            renderRow(contact, {
              key: `external-${contact.id}`,
              mention: null,
              onEdit: () =>
                openModal('billing_contact_external_edit', {
                  idCompany,
                  contact,
                }),
            }),
          ),
        )}
      </>
    );
  };

  return (
    <PanelCard
      title={text('card.title', 'Notifications')}
      collapsible
      badge={addButton}
      contentClassName={'px-4 py-3 flex flex-col gap-4'}
    >
      {renderContent()}
    </PanelCard>
  );
};

export default CompanyBillingNotificationsCard;
